/* eslint-disable max-len */
import { DateRange, DateRangeValidationError } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';

export interface DateRangeValidationHookProps {
  schoolYearStartDate?: Dayjs;
  schoolYearEndDate?: Dayjs;
  defaultStartDate?: Dayjs;
  defaultEndDate?: Dayjs;
}

export interface DateRangeValidationHookResult {
  error: DateRangeValidationError;
  dateRangeValue: DateRange<Dayjs>;
  errorMessage: string;
  handleChange: (newValue: DateRange<Dayjs>) => void;
  handleDatePickerError: (errors: DateRangeValidationError) => void;
  setDateRangeValue: React.Dispatch<React.SetStateAction<DateRange<Dayjs>>>;
  resetDateRange: () => void;
  isValid: boolean;
}

const defaultRollingYearBounds = (): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().startOf('day');
  const start = end.subtract(1, 'year').add(1, 'day');
  return { start, end };
};

export const useDateRangeValidation = (
  props: DateRangeValidationHookProps = {}
): DateRangeValidationHookResult => {
  const rolled = defaultRollingYearBounds();
  const {
    schoolYearStartDate = rolled.start,
    schoolYearEndDate = rolled.end,
    defaultStartDate = dayjs().subtract(30, 'day'),
    defaultEndDate = dayjs(),
  } = props;

  const [error, setError] = useState<DateRangeValidationError>([null, null]);
  const [customError, setCustomError] = useState<null | string>('');
  const [dateRangeValue, setDateRangeValue] = useState<DateRange<Dayjs>>([
    defaultStartDate,
    defaultEndDate,
  ]);

  const isStartDateLessThanEndDate = useCallback(
    (startDate: Dayjs | null, endDate: Dayjs | null): boolean | undefined => {
      if (!startDate?.isValid() || !endDate?.isValid()) return undefined;
      const s = startDate.startOf('day');
      const e = endDate.startOf('day');
      return s.isBefore(e) || s.isSame(e);
    },
    []
  );

  const isDateValid = useCallback((date: Dayjs | null): boolean | undefined => {
    return date?.isValid();
  }, []);

  const isDateInRangeAndValid = useCallback(
    (date: Dayjs | null): boolean | undefined => {
      if (!date?.isValid()) return false;
      const d = date.startOf('day');
      const min = schoolYearStartDate.startOf('day');
      const max = schoolYearEndDate.startOf('day');
      return (
        (d.isSame(min) || d.isAfter(min)) && (d.isSame(max) || d.isBefore(max))
      );
    },
    [schoolYearStartDate, schoolYearEndDate]
  );

  const getErrorMessage = useCallback((): string => {
    let tempErrorMessage = '';

    switch (customError) {
      case 'invalidStartDate': {
        tempErrorMessage = 'Start date is invalid.';
        break;
      }
      case 'invalidEndDate': {
        tempErrorMessage = 'End date is invalid.';
        break;
      }
      case 'disableFuture': {
        tempErrorMessage = 'End Date cannot exceed the current date';
        break;
      }
      case 'outOfOrder': {
        tempErrorMessage = 'Start date cannot be greater than end date.';
        break;
      }
      case 'outOfRange': {
        tempErrorMessage = `Start & end date should be within ${schoolYearStartDate.format(
          'MM/DD/YYYY'
        )} - ${schoolYearEndDate.format('MM/DD/YYYY')} (365-day range).`;
        break;
      }
    }

    return tempErrorMessage;
  }, [customError, schoolYearStartDate, schoolYearEndDate]);

  const handleChange = useCallback(
    (newValue: DateRange<Dayjs>): void => {
      const todayStart = dayjs().startOf('day');

      // Check for invalid dates first (most specific errors)
      if (!isDateValid(newValue[0])) {
        setCustomError('invalidStartDate');
      } else if (!isDateValid(newValue[1])) {
        setCustomError('invalidEndDate');
      }
      // Then check for future dates
      else if (newValue[1] && newValue[1].startOf('day').isAfter(todayStart)) {
        setCustomError('disableFuture');
      }
      // Then check if all validations pass
      else if (
        isDateInRangeAndValid(newValue[0]) &&
        isDateInRangeAndValid(newValue[1]) &&
        isStartDateLessThanEndDate(newValue[0], newValue[1])
      ) {
        setCustomError('');
      }
      // Check for date order issues
      else if (
        isDateInRangeAndValid(newValue[0]) &&
        isDateInRangeAndValid(newValue[1]) &&
        !isStartDateLessThanEndDate(newValue[0], newValue[1])
      ) {
        setCustomError('outOfOrder');
      }
      // Finally, check for range issues
      else if (
        !isDateInRangeAndValid(newValue[0]) ||
        !isDateInRangeAndValid(newValue[1])
      ) {
        setCustomError('outOfRange');
      } else {
        setError([null, null]);
      }

      setDateRangeValue(newValue);
    },
    [isDateInRangeAndValid, isStartDateLessThanEndDate, isDateValid]
  );

  const resetDateRange = useCallback(() => {
    setDateRangeValue([defaultStartDate, defaultEndDate]);
    setCustomError('');
    setError([null, null]);
  }, [defaultStartDate, defaultEndDate]);

  const handleDatePickerError = useCallback(
    ([startError, endError]: DateRangeValidationError): void => {
      setError([startError, endError]);
    },
    []
  );

  return {
    error,
    dateRangeValue,
    errorMessage: getErrorMessage(),
    handleChange,
    handleDatePickerError,
    setDateRangeValue,
    resetDateRange,
    isValid: customError === '' && error[0] === null && error[1] === null,
  };
};
